import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Edit, Plus, FolderPlus, Upload, Download, Trash2, FileText, Folder, MoreVertical, ChevronRight, Home, Paperclip, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { ClientForm } from './Clients';
import AttachmentPanel from '../components/AttachmentPanel';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TaxConfigPanel from '../components/TaxConfigPanel';
import ClientActivityConfig from '../components/ClientActivityConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRestrictedRole = user?.role === 'Colaborador';
  const [client, setClient] = useState(null);
  const [allFolders, setAllFolders] = useState([]);
  const [folderPath, setFolderPath] = useState([]); // breadcrumb: array of folder objects
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateDeadlineModal, setShowCreateDeadlineModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedDeadlineDetail, setSelectedDeadlineDetail] = useState(null);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  const [clientTasks, setClientTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [generatingDeadlines, setGeneratingDeadlines] = useState(false);

  // Current folder is the last item in the path, or null (root)
  const currentFolder = folderPath.length > 0 ? folderPath[folderPath.length - 1] : null;
  const currentFolderId = currentFolder?._id || null;

  useEffect(() => {
    loadClient();
    loadFolders();
    loadClientTasks();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (currentFolderId) {
      loadFiles(currentFolderId);
    } else {
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  const loadClient = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients/${clientId}`);
      setClient(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/folders`);
      setAllFolders(data);
    } catch (error) {
    }
  };

  const loadClientTasks = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/tasks`);
      setClientTasks(data.filter(t => t.client_id === clientId));
    } catch (error) {
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/users`);
      setUsers(data);
    } catch (error) {
    }
  };

  const handleGenerateAllDeadlines = async () => {
    if (!window.confirm('¿Generar todos los vencimientos del año para este cliente basado en sus impuestos configurados?')) return;
    setGeneratingDeadlines(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/clients/${clientId}/generate-deadlines`);
      alert(data.message + (data.errors?.length ? `\nErrores: ${data.errors.join(', ')}` : ''));
      loadClient();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al generar vencimientos');
    } finally {
      setGeneratingDeadlines(false);
    }
  };

  const handleChangeTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/tasks/${taskId}/status`, { estado: newStatus });
      loadClientTasks();
    } catch (error) {
      alert('Error al actualizar el estado');
    }
  };

  const handleDeleteDeadline = async (deadlineId) => {
    if (!window.confirm('¿Está seguro de eliminar este vencimiento?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/deadlines/${deadlineId}`);
      loadClient();
    } catch (error) {
      alert('Error al eliminar el vencimiento');
    }
  };

  const loadFiles = async (folderId) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/folders/${folderId}/files`);
      setFiles(data);
    } catch (error) {
    }
  };

  // Get visible folders for the current level
  const visibleFolders = allFolders.filter(f => {
    if (currentFolderId) {
      return f.parent_id === currentFolderId;
    }
    return !f.parent_id;
  });

  // Count subfolders for a given folder
  const getSubfolderCount = useCallback((folderId) => {
    return allFolders.filter(f => f.parent_id === folderId).length;
  }, [allFolders]);

  const navigateIntoFolder = (folder) => {
    setFolderPath(prev => [...prev, folder]);
  };

  const navigateUp = () => {
    setFolderPath(prev => prev.slice(0, -1));
  };

  const navigateToBreadcrumb = (index) => {
    // index === -1 means go to root
    if (index === -1) {
      setFolderPath([]);
    } else {
      setFolderPath(prev => prev.slice(0, index + 1));
    }
  };

  const handleCreateFolder = async (folderName) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/clients/${clientId}/folders`,
        { 
          name: folderName,
          parent_id: currentFolderId
        },
        {  }
      );
      loadFolders();
      setShowCreateFolderModal(false);
    } catch (error) {
      alert('Error al crear la carpeta');
    }
  };

  const handleRenameFolder = async (folder, newName) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/folders/${folder._id}`,
        { name: newName },
        {  }
      );
      loadFolders();
      // Update breadcrumb if the renamed folder is in the path
      setFolderPath(prev => prev.map(f => f._id === folder._id ? { ...f, name: newName } : f));
    } catch (error) {
      alert('Error al renombrar la carpeta');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('¿Está seguro de eliminar esta carpeta? Se eliminarán todas las subcarpetas y archivos dentro.')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/folders/${folderId}`);
      loadFolders();
      // If the deleted folder is in our path, navigate up past it
      const pathIndex = folderPath.findIndex(f => f._id === folderId);
      if (pathIndex !== -1) {
        setFolderPath(prev => prev.slice(0, pathIndex));
      }
    } catch (error) {
      alert('Error al eliminar la carpeta');
    }
  };

  const handleUploadFile = async (file) => {
    if (!currentFolder) {
      alert('Por favor selecciona una carpeta primero');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${BACKEND_URL}/api/folders/${currentFolder._id}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      loadFiles(currentFolder._id);
      setShowUploadModal(false);
    } catch (error) {
      alert('Error al subir el archivo');
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/files/${file.id}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al descargar el archivo');
    }
  };

  const handleRenameFile = async (file, newName) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/files/${file.id}`,
        { filename: newName },
        {  }
      );
      loadFiles(currentFolder._id);
    } catch (error) {
      alert('Error al renombrar el archivo');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('¿Está seguro de eliminar este archivo?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/files/${fileId}`);
      loadFiles(currentFolder._id);
    } catch (error) {
      alert('Error al eliminar el archivo');
    }
  };

  const getStatusBadge = (estado) => {
    const badges = {
      Pendiente: 'bg-indigo-100 text-indigo-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      Realizado: 'bg-emerald-100 text-emerald-800',
      Finalizada: 'bg-emerald-100 text-emerald-800',
      Vencido: 'bg-red-100 text-red-800',
    };
    return badges[estado] || 'bg-slate-100 text-slate-800';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cargando cliente...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cliente no encontrado</div>
      </div>
    );
  }

  const clientName = client.razon_social || `${client.primer_nombre || ''} ${client.primer_apellido || ''}`.trim();

  return (
    <div className="space-y-6" data-testid="client-detail">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/clients')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              {clientName}
            </h1>
            <p className="text-sm text-slate-700 leading-relaxed mt-2">Información completa del cliente</p>
          </div>
        </div>
        {!isRestrictedRole && (
          <Button
            onClick={() => setShowEditModal(true)}
            className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
            data-testid="edit-client-button"
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar Cliente
          </Button>
        )}
      </div>

      {/* Edit Client Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          <ClientForm
            client={client}
            onSuccess={() => { setShowEditModal(false); loadClient(); }}
          />
        </DialogContent>
      </Dialog>

      {/* Responsable Assignment */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex items-center gap-4">
        <span className="text-xs font-semibold tracking-wider uppercase text-slate-500">Responsable:</span>
        {!isRestrictedRole ? (
          <select
            value={client.responsable_id || ''}
            onChange={async (e) => {
              try {
                await axios.patch(`${BACKEND_URL}/api/clients/${clientId}/responsable`, { responsable_id: e.target.value || null });
                loadClient();
              } catch { alert('Error al asignar responsable'); }
            }}
            className="border border-slate-300 rounded-md px-3 py-1.5 text-sm"
            data-testid="client-responsable-select"
          >
            <option value="">Sin asignar</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </select>
        ) : (
          <span className="text-sm font-medium text-slate-800">{client.responsable_name || 'Sin asignar'}</span>
        )}
      </div>

      <Tabs defaultValue="info" className="w-full">
        <TabsList>
          <TabsTrigger value="info">Información</TabsTrigger>
          <TabsTrigger value="deadlines">Vencimientos</TabsTrigger>
          <TabsTrigger value="tasks">Tareas</TabsTrigger>
          {!isRestrictedRole && <TabsTrigger value="taxes">Impuestos</TabsTrigger>}
          {!isRestrictedRole && <TabsTrigger value="actividades">Actividades</TabsTrigger>}
          <TabsTrigger value="files">Archivos</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h2 className="text-2xl tracking-tight font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>
              Información del Cliente
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {client.tipo_persona === 'Jurídica' ? (
                <>
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Razón Social</p>
                    <p className="text-sm text-slate-900 mt-1">{client.razon_social}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">NIT</p>
                    <p className="text-sm text-slate-900 mt-1">{client.nit}-{client.dv}</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Nombre Completo</p>
                    <p className="text-sm text-slate-900 mt-1">
                      {client.primer_nombre} {client.segundo_nombre} {client.primer_apellido} {client.segundo_apellido}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Documento</p>
                    <p className="text-sm text-slate-900 mt-1">{client.tipo_documento} {client.numero_documento}</p>
                  </div>
                </>
              )}
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Email</p>
                <p className="text-sm text-slate-900 mt-1">{client.email}</p>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Teléfono</p>
                <p className="text-sm text-slate-900 mt-1">{client.telefono_principal}</p>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Ubicación</p>
                <p className="text-sm text-slate-900 mt-1">{client.ciudad}, {client.departamento}</p>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Dirección</p>
                <p className="text-sm text-slate-900 mt-1">{client.direccion}</p>
              </div>
              {client.actividades_economicas?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Actividades Económicas (CIIU)</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {client.actividades_economicas.map((codigo) => (
                      <span key={codigo} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs">
                        {codigo}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Backward compat: single actividad_economica */}
              {!client.actividades_economicas?.length && client.actividad_economica && (
                <div>
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Actividad Económica (CIIU)</p>
                  <p className="text-sm text-slate-900 mt-1">{client.actividad_economica}</p>
                </div>
              )}
              {client.representante_legal_nombre && (
                <>
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Representante Legal</p>
                    <p className="text-sm text-slate-900 mt-1">{client.representante_legal_nombre}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Cargo</p>
                    <p className="text-sm text-slate-900 mt-1">{client.representante_legal_cargo}</p>
                  </div>
                </>
              )}
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Estado</p>
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                  client.estado === 'Activo' ? 'bg-emerald-100 text-emerald-800' :
                  client.estado === 'Pausado' ? 'bg-indigo-100 text-indigo-800' :
                  'bg-slate-100 text-slate-800'
                }`}>
                  {client.estado}
                </span>
              </div>
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-slate-500">Responsabilidades Fiscales</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {client.responsabilidades_fiscales?.map((codigo) => (
                    <span key={codigo} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs">
                      {codigo}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="deadlines" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>
                Vencimientos Asociados
              </h2>
              {!isRestrictedRole && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleGenerateAllDeadlines}
                    disabled={generatingDeadlines}
                    variant="outline"
                    className="border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white"
                    data-testid="generate-all-deadlines-button"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {generatingDeadlines ? 'Generando...' : 'Generar Todo'}
                  </Button>
                  <Dialog open={showCreateDeadlineModal} onOpenChange={setShowCreateDeadlineModal}>
                  <Button
                    onClick={() => setShowCreateDeadlineModal(true)}
                    className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
                    data-testid="create-deadline-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Vencimiento
                  </Button>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Nuevo Vencimiento — {clientName}</DialogTitle>
                  </DialogHeader>
                  <InlineDeadlineForm
                    clientId={clientId}
                    client={client}
                    users={users}
                    onSuccess={() => { setShowCreateDeadlineModal(false); loadClient(); }}
                  />
                </DialogContent>
              </Dialog>
                </div>
              )}
            </div>

            {client.deadlines && client.deadlines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="pb-3">Tipo de Impuesto</th>
                      <th className="pb-3">Período</th>
                      <th className="pb-3">Fecha Vencimiento</th>
                      <th className="pb-3">Valor Estimado</th>
                      <th className="pb-3">Estado</th>
                      <th className="pb-3 text-center">Adjuntos</th>
                      <th className="pb-3 text-center">Eliminar</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-slate-700">
                    {client.deadlines.map((deadline) => (
                      <tr key={deadline._id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3">{deadline.tipo_display || deadline.tipo_impuesto}</td>
                        <td className="py-3">{deadline.periodo_gravable}</td>
                        <td className="py-3">{deadline.fecha_vencimiento}</td>
                        <td className="py-3">
                          {deadline.valor_estimado ? `$${deadline.valor_estimado.toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(deadline.estado)}`}>
                            {deadline.estado}
                          </span>
                        </td>
                        <td className="py-3 text-center">
                          <button
                            onClick={() => setSelectedDeadlineDetail(deadline)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Ver adjuntos"
                            data-testid={`client-deadline-attach-${deadline._id}`}
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                        </td>
                        <td className="py-3 text-center">
                          <button
                            onClick={() => handleDeleteDeadline(deadline._id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Eliminar vencimiento"
                            data-testid={`client-deadline-delete-${deadline._id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">No hay vencimientos asociados</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="tasks" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl tracking-tight font-semibold" style={{ fontFamily: 'Outfit' }}>
                Tareas Asociadas
              </h2>
              {!isRestrictedRole && (
                <Dialog open={showCreateTaskModal} onOpenChange={setShowCreateTaskModal}>
                  <Button
                    onClick={() => setShowCreateTaskModal(true)}
                    className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
                    data-testid="create-task-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Tarea
                  </Button>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Nueva Tarea — {clientName}</DialogTitle>
                  </DialogHeader>
                  <InlineTaskForm
                    clientId={clientId}
                    users={users}
                    onSuccess={() => { setShowCreateTaskModal(false); loadClientTasks(); }}
                  />
                </DialogContent>
              </Dialog>
              )}
            </div>

            {clientTasks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="pb-3">Título</th>
                      <th className="pb-3">Responsable</th>
                      <th className="pb-3">Fecha Límite</th>
                      <th className="pb-3">Prioridad</th>
                      <th className="pb-3">Estado</th>
                      <th className="pb-3 text-center">Adjuntos</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm text-slate-700">
                    {clientTasks.map((task) => (
                      <tr key={task._id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 font-medium text-slate-900">{task.titulo}</td>
                        <td className="py-3">{task.responsable_name || '-'}</td>
                        <td className="py-3">{task.fecha_limite}</td>
                        <td className="py-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            task.prioridad === 'Alta' ? 'bg-red-100 text-red-800' :
                            task.prioridad === 'Media' ? 'bg-blue-100 text-blue-800' :
                            'bg-slate-100 text-slate-800'
                          }`}>
                            {task.prioridad}
                          </span>
                        </td>
                        <td className="py-3">
                          <select
                            value={task.estado}
                            onChange={(e) => handleChangeTaskStatus(task._id, e.target.value)}
                            className={`text-xs font-medium rounded-full px-2.5 py-1 border-0 cursor-pointer focus:ring-2 focus:ring-blue-300 ${getStatusBadge(task.estado)}`}
                            data-testid={`task-status-${task._id}`}
                          >
                            <option value="Pendiente">Pendiente</option>
                            <option value="En proceso">En proceso</option>
                            <option value="Finalizada">Finalizada</option>
                          </select>
                        </td>
                        <td className="py-3 text-center">
                          <button
                            onClick={() => setSelectedTaskDetail(task)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Ver adjuntos"
                            data-testid={`client-task-attach-${task._id}`}
                          >
                            <Paperclip className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">No hay tareas asociadas a este cliente</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="taxes" className="mt-6">
          <TaxConfigPanel clientId={clientId} />
        </TabsContent>

        <TabsContent value="actividades" className="mt-6">
          <ClientActivityConfig clientId={clientId} />
        </TabsContent>

        <TabsContent value="files" className="mt-6">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-1 mb-4 px-1 flex-wrap" data-testid="folder-breadcrumb">
            <button
              onClick={() => navigateToBreadcrumb(-1)}
              className={`flex items-center gap-1 text-sm px-2 py-1 rounded transition-colors ${
                folderPath.length === 0 
                  ? 'text-slate-900 font-semibold bg-slate-100' 
                  : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
              }`}
              data-testid="breadcrumb-root"
            >
              <Home className="w-3.5 h-3.5" />
              <span>Raíz</span>
            </button>
            {folderPath.map((folder, index) => (
              <React.Fragment key={folder._id}>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <button
                  onClick={() => navigateToBreadcrumb(index)}
                  className={`text-sm px-2 py-1 rounded transition-colors truncate max-w-[160px] ${
                    index === folderPath.length - 1
                      ? 'text-slate-900 font-semibold bg-slate-100'
                      : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                  }`}
                  title={folder.name}
                  data-testid={`breadcrumb-folder-${index}`}
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Folder Tree */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {currentFolder ? `Carpetas en "${currentFolder.name}"` : 'Carpetas'}
                </h3>
                <Dialog open={showCreateFolderModal} onOpenChange={setShowCreateFolderModal}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="create-folder-btn">
                      <FolderPlus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {currentFolder ? `Nueva subcarpeta en "${currentFolder.name}"` : 'Nueva Carpeta'}
                      </DialogTitle>
                    </DialogHeader>
                    <CreateFolderForm onSubmit={handleCreateFolder} />
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-1">
                {folderPath.length > 0 && (
                  <button
                    onClick={navigateUp}
                    className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded flex items-center gap-2 transition-colors"
                    data-testid="folder-go-back"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Volver a {folderPath.length > 1 ? `"${folderPath[folderPath.length - 2].name}"` : 'Raíz'}
                  </button>
                )}
                {visibleFolders.length === 0 && (
                  <div className="text-center py-6 text-slate-400">
                    <Folder className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-xs">No hay carpetas aquí</p>
                    <p className="text-xs mt-1">Crea una con el botón +</p>
                  </div>
                )}
                {visibleFolders.map((folder) => {
                  const subCount = getSubfolderCount(folder._id);
                  return (
                    <div
                      key={folder._id}
                      className="flex items-center justify-between px-3 py-2 rounded cursor-pointer hover:bg-slate-50 group transition-colors"
                      data-testid={`folder-item-${folder._id}`}
                    >
                      <div
                        className="flex items-center gap-2 flex-1 min-w-0"
                        onClick={() => navigateIntoFolder(folder)}
                      >
                        <Folder className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-sm truncate">{folder.name}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {subCount > 0 && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">
                              {subCount} sub
                            </span>
                          )}
                          <span className="text-xs text-slate-500">({folder.file_count || 0})</span>
                        </div>
                      </div>
                      <FolderMenu 
                        folder={folder}
                        onRename={handleRenameFolder}
                        onDelete={handleDeleteFolder}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Files List */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {currentFolder ? `Archivos en "${currentFolder.name}"` : 'Archivos'}
                </h3>
                {currentFolder && (
                  <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="upload-file-btn">
                        <Upload className="w-4 h-4 mr-2" />
                        Subir Archivo
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Subir Archivo a "{currentFolder.name}"</DialogTitle>
                      </DialogHeader>
                      <UploadForm onUpload={handleUploadFile} />
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {!currentFolder ? (
                <div className="text-center py-12 text-slate-500">
                  <Folder className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Selecciona una carpeta para ver sus archivos</p>
                </div>
              ) : files.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay archivos en esta carpeta</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {files.map((file) => (
                    <div
                      key={file._id}
                      className="flex items-center justify-between px-4 py-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                      data-testid={`file-item-${file.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{file.original_filename}</p>
                          <p className="text-xs text-slate-500">
                            {formatFileSize(file.size)} · {new Date(file.created_at).toLocaleDateString('es-CO')}
                          </p>
                        </div>
                      </div>
                      <FileMenu 
                        file={file}
                        onDownload={handleDownloadFile}
                        onRename={handleRenameFile}
                        onDelete={handleDeleteFile}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

      {/* Deadline Attachment Dialog */}
      <Dialog open={!!selectedDeadlineDetail} onOpenChange={(open) => { if (!open) setSelectedDeadlineDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adjuntos — {selectedDeadlineDetail?.tipo_display || selectedDeadlineDetail?.tipo_impuesto}</DialogTitle>
          </DialogHeader>
          {selectedDeadlineDetail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-xs text-slate-500">Período:</span> <span className="font-medium">{selectedDeadlineDetail.periodo_gravable}</span></div>
                <div><span className="text-xs text-slate-500">Vence:</span> <span className="font-medium">{selectedDeadlineDetail.fecha_vencimiento}</span></div>
              </div>
              <div className="border-t pt-3">
                <AttachmentPanel entityType="deadline" entityId={selectedDeadlineDetail._id} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Attachment Dialog */}
      <Dialog open={!!selectedTaskDetail} onOpenChange={(open) => { if (!open) setSelectedTaskDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Adjuntos — {selectedTaskDetail?.titulo}</DialogTitle>
          </DialogHeader>
          {selectedTaskDetail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-xs text-slate-500">Estado:</span> <span className="font-medium">{selectedTaskDetail.estado}</span></div>
                <div><span className="text-xs text-slate-500">Fecha límite:</span> <span className="font-medium">{selectedTaskDetail.fecha_limite}</span></div>
              </div>
              <div className="border-t pt-3">
                <AttachmentPanel entityType="task" entityId={selectedTaskDetail._id} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      </Tabs>
    </div>
  );
};

// Helper Components

// --- Inline Deadline Form (with DIAN auto-calculation) ---
const TIPOS_IMPUESTO = [
  'IVA — Impuesto sobre las Ventas',
  'Retención en la Fuente Nacional',
  'Retención a Título de ICA',
  'Impuesto de Industria y Comercio (ICA)',
  'Exógena Nacional (Información Exógena DIAN)',
  'Declaración de Renta',
  'Activos en el Exterior',
  'Régimen Simple de Tributación — Anticipo bimestral',
  'Régimen Simple de Tributación — Declaración anual',
  'Exógena Municipal',
  'Impuesto Alumbrado Público — Yumbo',
  'Información Cambiaria (Banco de la República)',
  'Información Financiera Supersociedades',
];

const InlineDeadlineForm = ({ clientId, client, users, onSuccess }) => {
  const [clientTaxConfigs, setClientTaxConfigs] = useState([]);
  const [formData, setFormData] = useState({
    client_id: clientId,
    tipo_impuesto: '',
    periodo_gravable: '',
    fecha_vencimiento: '',
    fecha_limite_sancion: '',
    valor_estimado: '',
    responsable_id: '',
    notas: '',
    estado: 'Pendiente',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [periodos, setPeriodos] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [calculando, setCalculando] = useState(false);

  useEffect(() => {
    loadTaxConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (formData.tipo_impuesto) {
      calcularPeriodos(formData.tipo_impuesto);
    } else {
      setPeriodos([]);
      setAlertas([]);
      setFormData(prev => ({ ...prev, periodo_gravable: '', fecha_vencimiento: '', fecha_limite_sancion: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tipo_impuesto]);

  const loadTaxConfigs = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/clients/${clientId}/tax-config`);
      setClientTaxConfigs(data.filter(c => c.activo !== false));
    } catch (error) {
    }
  };

  const calcularPeriodos = async (tipoImpuesto) => {
    setCalculando(true);
    setAlertas([]);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/utils/calcular-vencimiento`,
        { client_id: clientId, tipo_impuesto: tipoImpuesto },
        {  }
      );
      setPeriodos(data.periodos || []);
      setAlertas(data.alertas || []);
      if (data.periodos?.length === 1) {
        const p = data.periodos[0];
        setFormData(prev => ({
          ...prev,
          periodo_gravable: p.periodo,
          fecha_vencimiento: p.fecha_vencimiento,
          fecha_limite_sancion: p.fecha_sancion || '',
        }));
      } else {
        setFormData(prev => ({ ...prev, periodo_gravable: '', fecha_vencimiento: '', fecha_limite_sancion: '' }));
      }
    } catch (error) {
      setAlertas(['Error al calcular los periodos. Ingrese las fechas manualmente.']);
    } finally {
      setCalculando(false);
    }
  };

  const handleSelectPeriodo = (periodo) => {
    const found = periodos.find(p => p.periodo === periodo);
    if (found) {
      setFormData(prev => ({
        ...prev,
        periodo_gravable: found.periodo,
        fecha_vencimiento: found.fecha_vencimiento,
        fecha_limite_sancion: found.fecha_sancion || '',
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        ...formData,
        valor_estimado: formData.valor_estimado ? parseFloat(formData.valor_estimado) : null,
      };
      await axios.post(`${BACKEND_URL}/api/deadlines`, payload);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear el vencimiento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="inline-deadline-form">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>
      )}
      {alertas.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-1">
          {alertas.map((alerta, idx) => (
            <p key={idx} className="text-sm text-blue-800 flex items-start gap-2">
              <span className="font-bold text-blue-600 flex-shrink-0">!</span>
              {alerta}
            </p>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Tipo de Impuesto *</Label>
          <select name="tipo_impuesto" value={formData.tipo_impuesto} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required
            data-testid="inline-tipo-impuesto-select">
            <option value="">Seleccionar tipo...</option>
            {clientTaxConfigs.length > 0 ? (
              <>
                <optgroup label="Impuestos configurados">
                  {clientTaxConfigs.map(tc => (
                    <option key={tc._id} value={tc.tipo_impuesto}>
                      {tc.municipio ? `${tc.tipo_impuesto} — ${tc.municipio}` : tc.tipo_impuesto}
                      {tc.periodicidad ? ` (${tc.periodicidad})` : ''}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Otros impuestos">
                  {TIPOS_IMPUESTO.filter(t => !clientTaxConfigs.some(tc => tc.tipo_impuesto === t)).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </optgroup>
              </>
            ) : (
              TIPOS_IMPUESTO.map(t => <option key={t} value={t}>{t}</option>)
            )}
          </select>
        </div>

        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
            Período Gravable *
            {calculando && <span className="ml-2 text-blue-500 text-[10px] font-normal">Calculando...</span>}
          </Label>
          {periodos.length > 0 ? (
            <select value={formData.periodo_gravable} onChange={(e) => handleSelectPeriodo(e.target.value)}
              className="flex h-10 w-full rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-900" required
              data-testid="inline-periodo-select">
              <option value="">Seleccionar periodo...</option>
              {periodos.map(p => (
                <option key={p.periodo} value={p.periodo}>{p.periodo} (Vence: {p.fecha_vencimiento})</option>
              ))}
            </select>
          ) : (
            <Input name="periodo_gravable" value={formData.periodo_gravable} onChange={handleChange} required
              placeholder="Ej: Enero 2026, Bimestre 1 (Ene-Feb 2026)" data-testid="inline-periodo-input" />
          )}
        </div>

        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
            Fecha de Vencimiento *
            {formData.fecha_vencimiento && periodos.length > 0 && (
              <span className="ml-2 text-emerald-600 text-[10px] font-normal">DIAN Auto</span>
            )}
          </Label>
          <Input type="date" name="fecha_vencimiento" value={formData.fecha_vencimiento} onChange={handleChange} required
            className={periodos.length > 0 && formData.fecha_vencimiento ? 'border-emerald-300 bg-emerald-50' : ''} />
        </div>

        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">
            Fecha Límite con Sanción
            {formData.fecha_limite_sancion && periodos.length > 0 && (
              <span className="ml-2 text-emerald-600 text-[10px] font-normal">DIAN Auto</span>
            )}
          </Label>
          <Input type="date" name="fecha_limite_sancion" value={formData.fecha_limite_sancion} onChange={handleChange}
            className={periodos.length > 0 && formData.fecha_limite_sancion ? 'border-emerald-300 bg-emerald-50' : ''} />
        </div>

        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Valor Estimado (COP)</Label>
          <Input type="number" name="valor_estimado" value={formData.valor_estimado} onChange={handleChange} placeholder="0" />
        </div>

        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Responsable *</Label>
          <select name="responsable_id" value={formData.responsable_id} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required
            data-testid="inline-responsable-select">
            <option value="">Seleccionar responsable...</option>
            {users.map(usr => (
              <option key={usr._id} value={usr._id}>{usr.name} - {usr.role}</option>
            ))}
          </select>
        </div>

        <div>
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Estado</Label>
          <select name="estado" value={formData.estado} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="Pendiente">Pendiente</option>
            <option value="En proceso">En proceso</option>
            <option value="Realizado">Realizado</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500 block mb-2">Notas</Label>
          <textarea name="notas" value={formData.notas} onChange={handleChange}
            className="flex min-h-[60px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Observaciones..." />
        </div>
      </div>
      <div className="flex justify-end pt-4 border-t">
        <Button type="submit" disabled={loading} className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="inline-submit-deadline">
          {loading ? 'Creando...' : 'Crear Vencimiento'}
        </Button>
      </div>
    </form>
  );
};

// --- Inline Task Form ---
const InlineTaskForm = ({ clientId, users, onSuccess }) => {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    client_id: clientId,
    responsable_id: '',
    fecha_limite: '',
    prioridad: 'Media',
    estado: 'Pendiente',
    observaciones: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/tasks`, formData);
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al crear la tarea');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="inline-task-form">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Título *</Label>
          <Input name="titulo" value={formData.titulo} onChange={handleChange} required placeholder="Título de la tarea"
            data-testid="inline-task-titulo" />
        </div>
        <div className="md:col-span-2">
          <Label>Descripción</Label>
          <textarea name="descripcion" value={formData.descripcion} onChange={handleChange}
            className="flex min-h-[70px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Descripción detallada de la tarea" />
        </div>
        <div>
          <Label>Responsable *</Label>
          <select name="responsable_id" value={formData.responsable_id} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required
            data-testid="inline-task-responsable">
            <option value="">Seleccionar responsable...</option>
            {users.map(usr => (
              <option key={usr._id} value={usr._id}>{usr.name} - {usr.role}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Fecha Límite *</Label>
          <Input type="date" name="fecha_limite" value={formData.fecha_limite} onChange={handleChange} required
            data-testid="inline-task-fecha" />
        </div>
        <div>
          <Label>Prioridad *</Label>
          <select name="prioridad" value={formData.prioridad} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required>
            <option value="Alta">Alta</option>
            <option value="Media">Media</option>
            <option value="Baja">Baja</option>
          </select>
        </div>
        <div>
          <Label>Estado *</Label>
          <select name="estado" value={formData.estado} onChange={handleChange}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" required>
            <option value="Pendiente">Pendiente</option>
            <option value="En proceso">En proceso</option>
            <option value="Finalizada">Finalizada</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <Label>Observaciones</Label>
          <textarea name="observaciones" value={formData.observaciones} onChange={handleChange}
            className="flex min-h-[50px] w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
            placeholder="Notas adicionales" />
        </div>
      </div>
      <div className="flex justify-end pt-4 border-t">
        <Button type="submit" disabled={loading} className="bg-[#1F3864] hover:bg-[#172b4d] text-white" data-testid="inline-submit-task">
          {loading ? 'Creando...' : 'Crear Tarea'}
        </Button>
      </div>
    </form>
  );
};

const CreateFolderForm = ({ onSubmit }) => {
  const [name, setName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSubmit(name.trim());
      setName('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-folder-form">
      <div>
        <Label>Nombre de la carpeta</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Mi carpeta"
          required
          data-testid="folder-name-input"
        />
      </div>
      <div className="flex justify-end">
        <Button type="submit" data-testid="create-folder-submit">Crear Carpeta</Button>
      </div>
    </form>
  );
};

const UploadForm = ({ onUpload }) => {
  const [file, setFile] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) {
      onUpload(file);
      setFile(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="upload-file-form">
      <div>
        <Label>Seleccionar archivo</Label>
        <Input
          type="file"
          onChange={(e) => setFile(e.target.files[0])}
          required
          data-testid="file-input"
        />
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={!file} data-testid="upload-file-submit">Subir Archivo</Button>
      </div>
    </form>
  );
};

const FolderMenu = ({ folder, onRename, onDelete }) => {
  const [showRename, setShowRename] = useState(false);
  const [newName, setNewName] = useState(folder.name);

  const handleRename = () => {
    if (newName.trim() && newName !== folder.name) {
      onRename(folder, newName.trim());
    }
    setShowRename(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" data-testid={`folder-menu-${folder._id}`}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setShowRename(true)}>
            Renombrar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDelete(folder._id)} className="text-red-600">
            Eliminar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renombrar Carpeta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nuevo nombre</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRename(false)}>Cancelar</Button>
              <Button onClick={handleRename}>Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const FileMenu = ({ file, onDownload, onRename, onDelete }) => {
  const [showRename, setShowRename] = useState(false);
  const [newName, setNewName] = useState(file.original_filename);

  const handleRename = () => {
    if (newName.trim() && newName !== file.original_filename) {
      onRename(file, newName.trim());
    }
    setShowRename(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" data-testid={`file-menu-${file.id}`}>
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onDownload(file)}>
            <Download className="w-4 h-4 mr-2" />
            Descargar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowRename(true)}>
            Renombrar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDelete(file.id)} className="text-red-600">
            <Trash2 className="w-4 h-4 mr-2" />
            Eliminar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renombrar Archivo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nuevo nombre</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRename(false)}>Cancelar</Button>
              <Button onClick={handleRename}>Guardar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientDetail;
